import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Border,
  ChipStatus,
  Colors,
  Divider,
  Icons,
  Typography,
  createClasses,
  pxToRem,
  spacing,
} from '@kp/react-ui';

import type {
  AlarmSeverityCounts,
  AlarmSeverityLevel,
} from '../../types/home-types';

import { KpiCard, type KpiCardProps } from './KpiCard';
import { KpiCardEmpty } from './KpiCardEmpty';
import { KpiCardHeader } from './KpiCardHeader';

const SEVERITY_ORDER: AlarmSeverityLevel[] = ['high', 'medium', 'low'];

const dot = {
  width: pxToRem(12),
  height: pxToRem(12),
  borderRadius: Border.Radius.Small,
  flexShrink: 0,
} as const;

const classes = createClasses({
  columns: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: spacing('xl'),
    '@media (max-width: 480px)': {
      gridTemplateColumns: '1fr',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('xs'),
  },
  emptyColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing('md'),
  },
  valueRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing('sm'),
  },
  severities: {
    display: 'flex',
    gap: spacing('sm'),
  },
  severity: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing('xxs'),
  },
  dotHigh: { ...dot, backgroundColor: Colors.Danger.icon },
  dotMedium: { ...dot, backgroundColor: Colors.KiebackPeter.kpOrange },
  dotLow: { ...dot, backgroundColor: Colors.Palette.green },
  weak: {
    color: Colors.Neutral.textWeak,
  },
  inactive: {
    color: Colors.Neutral.textInactive,
  },
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    whiteSpace: 'nowrap',
  },
});

const SEVERITY_DOT_CLASS: Record<AlarmSeverityLevel, string> = {
  high: classes.dotHigh,
  medium: classes.dotMedium,
  low: classes.dotLow,
};

export interface AlarmsFindingsCardProps
  extends Pick<
    KpiCardProps,
    'href' | 'isLoading' | 'isError' | 'isEmpty' | 'onRetry' | 'className'
  > {
  activeCount: number;
  findingsCount: number;
  bySeverity: AlarmSeverityCounts;
  notDiagnosed: number;
}

export const AlarmsFindingsCard: React.FC<AlarmsFindingsCardProps> = ({
  activeCount,
  findingsCount,
  bySeverity,
  notDiagnosed,
  ...cardProps
}) => {
  const { t } = useTranslation();
  const title = t('home.alarms.title');

  const severityLabel: Record<AlarmSeverityLevel, string> = {
    high: t('home.alarms.severity.high'),
    medium: t('home.alarms.severity.medium'),
    low: t('home.alarms.severity.low'),
  };

  return (
    <KpiCard
      header={
        <div className={classes.columns}>
          <KpiCardHeader
            icon={Icons.IconNotifications}
            title={title}
            subtitle={t('home.alarms.subtitle')}
          />
          <KpiCardHeader
            title={t('home.findings.title')}
            subtitle={t('home.findings.subtitle')}
          />
        </div>
      }
      linkLabel={t('home.openIn', { section: title })}
      footer={<ChipStatus state="ok" label={t('home.alarms.realTime')} />}
      emptyState={
        <div className={classes.columns}>
          <div className={classes.emptyColumn}>
            <Divider />
            <KpiCardEmpty />
          </div>
          <div className={classes.emptyColumn}>
            <Divider />
            <KpiCardEmpty />
          </div>
        </div>
      }
      {...cardProps}
    >
      <div className={classes.columns}>
        <div className={classes.column}>
          <div className={classes.valueRow}>
            <Typography variant="headlineMedium">{activeCount}</Typography>
            <div className={classes.severities}>
              {SEVERITY_ORDER.map((severity) => (
                <div key={severity} className={classes.severity}>
                  <span
                    className={SEVERITY_DOT_CLASS[severity]}
                    aria-hidden="true"
                  />
                  <span className={classes.srOnly}>
                    {severityLabel[severity]}
                  </span>
                  <Typography variant="labelSmall" className={classes.weak}>
                    {bySeverity[severity]}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
          <Typography variant="bodySmall" className={classes.inactive}>
            {t('home.alarms.notDiagnosed', { count: notDiagnosed })}
          </Typography>
        </div>
        <div className={classes.column}>
          <div className={classes.valueRow}>
            <Typography variant="headlineMedium">{findingsCount}</Typography>
            <Typography variant="bodySmall" className={classes.inactive}>
              {t('home.findings.open')}
            </Typography>
          </div>
        </div>
      </div>
    </KpiCard>
  );
};
